"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import HeaderWrapper from "@/components/HeaderWrapper";
import Footer from "@/components/Footer";
import ServiceTypeButtons from "@/components/ServiceTypeButtons";
import ServiceGrid from "@/components/ServiceGrid";
import ServiceList from "@/components/ServiceList";
import AppointmentCalendar from "@/components/AppointmentCalendar";
import ChatForm from "@/components/ChatForm";
import CustomRequestForm from "@/components/CustomRequestForm";
import { useSuppressBitwarden } from '@/lib/hydrationFix';

export const dynamic = 'force-dynamic';

export default function Home() {
  // Supprimer les attributs Bitwarden côté client
  useSuppressBitwarden();
  
  const router = useRouter();
  const searchParams = useSearchParams();
  
  // États pour la navigation par pages
  const [currentPage, setCurrentPage] = useState<'home' | 'services' | 'details' | 'calendar' | 'contact' | 'custom'>('home');
  
  const [serviceType, setServiceType] = useState<'installation' | 'reparation' | null>(null);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [selectedServiceOption, setSelectedServiceOption] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [showChat, setShowChat] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [showCustomForm, setShowCustomForm] = useState<boolean>(false);
  const [isCustomRequest, setIsCustomRequest] = useState<boolean>(false);

  // Gérer la navigation par URL
  useEffect(() => {
    const page = searchParams.get('page') || 'home';
    const type = searchParams.get('type') as 'installation' | 'reparation' | null;
    const service = searchParams.get('service');
    const option = searchParams.get('option');
    const custom = searchParams.get('custom') === 'true';
    
    setCurrentPage(page as any);
    
    if (type) setServiceType(type);
    if (service) setSelectedService(service);
    if (option) setSelectedServiceOption(option);
    if (custom) setIsCustomRequest(true);
    
    // Synchroniser les états d'affichage avec la page
    setShowDetails(page === 'details');
    setShowCalendar(page === 'calendar');
    setShowChat(page === 'contact');
    setShowCustomForm(page === 'custom');
  }, [searchParams]);

  // Fonction pour naviguer avec mise à jour URL
  const navigateToPage = (page: string, params: Record<string, string> = {}) => {
    const url = new URLSearchParams();
    url.set('page', page);
    
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.set(key, value);
    });
    
    router.push(`/?${url.toString()}`);
    setCurrentPage(page as any);
  };

  // Gestionnaire pour le bouton retour du navigateur
  useEffect(() => {
    const handlePopState = () => {
      const page = new URLSearchParams(window.location.search).get('page') || 'home';
      setCurrentPage(page as any);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Réinitialiser la sélection
  const resetSelection = () => {
    setServiceType(null);
    setSelectedService(null);
    setSelectedServiceOption(null);
    setShowDetails(false);
    setShowCalendar(false);
    setShowChat(false);
    setSelectedDate(null);
    setSelectedTime(null);
    setShowCustomForm(false);
    setIsCustomRequest(false);
    navigateToPage('home');
  };

  // Réinitialiser la sélection de service
  const resetServiceSelection = () => {
    setSelectedService(null);
    setSelectedServiceOption(null);
    setShowDetails(false);
    navigateToPage('services', { type: serviceType || '' });
  };

  // Retour du calendrier vers les détails
  const backFromCalendar = () => {
    setShowCalendar(false);
    setSelectedDate(null);
    setSelectedTime(null);
    navigateToPage('details', { 
      type: serviceType || '', 
      service: selectedService || '' 
    });
  };

  // Retour à l'étape calendrier depuis le chat
  const backFromChat = () => {
    setShowChat(false);
    navigateToPage('calendar', { 
      type: serviceType || '', 
      service: selectedService || '',
      option: selectedServiceOption || ''
    });
  };

  // Sélectionner un service
  const handleServiceSelect = (service: string) => {
    setSelectedService(service);
    setShowDetails(true);
    navigateToPage('details', { 
      type: serviceType || '', 
      service: service 
    });
  };

  // Sélectionner une option de service
  const handleServiceOption = (serviceOption: string) => {
    setSelectedServiceOption(serviceOption);
    setShowCalendar(true);
    navigateToPage('calendar', { 
      type: serviceType || '', 
      service: selectedService || '',
      option: serviceOption
    });
  };

  // Gérer la sélection de date depuis le calendrier
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };

  // Gérer la sélection d'horaire
  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
  };

  // Passer à l'affichage du formulaire de contact
  const handleProceedToContact = () => {
    if (selectedDate && selectedTime) {
      setShowCalendar(false);
      setShowChat(true);
      navigateToPage('contact', { 
        type: serviceType || '', 
        service: selectedService || '',
        option: selectedServiceOption || ''
      });
    } else {
      alert("Veuillez sélectionner une date et un horaire");
    }
  };

  // Gérer la demande personnalisée
  const handleCustomRequest = () => {
    setIsCustomRequest(true);
    setShowCustomForm(true);
    navigateToPage('custom', { custom: 'true' });
  };

  // Gérer la sélection du type de service avec navigation
  const handleServiceTypeSelect = (type: 'installation' | 'reparation') => {
    setServiceType(type);
    navigateToPage('services', { type });
  };

  // Gérer la sélection d'une sous-prestation
  const handleSubServiceSelect = (subService: string) => {
    setSelectedServiceOption(subService);
    navigateToPage('calendar', { 
      type: serviceType || '', 
      service: selectedService || '',
      option: subService
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" suppressHydrationWarning>
      <HeaderWrapper />
      
      <main className="flex-grow relative px-4 py-8 max-w-4xl mx-auto w-full" suppressHydrationWarning>
        <div className="relative" suppressHydrationWarning>
          {/* Bouton retour amélioré - centré */}
          {currentPage !== 'home' && (
            <div className="mb-6 flex justify-center" suppressHydrationWarning>
              <button 
                onClick={() => {
                  if (currentPage === 'contact') backFromChat();
                  else if (currentPage === 'calendar') backFromCalendar();
                  else if (currentPage === 'details') resetServiceSelection();
                  else if (currentPage === 'services') resetSelection();
                  else if (currentPage === 'custom') resetSelection();
                  else resetSelection();
                }}
                className="inline-flex items-center px-4 py-2 w-48 justify-center bg-white border-2 border-primary text-primary rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-opacity-50 active:transform active:scale-95 transition-transform duration-300 shadow-md font-medium"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                <span className="truncate">Retour</span>
              </button>
            </div>
          )}
        </div>
        
        {/* Éléments décoratifs */}
        <div className="absolute -top-20 right-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-0 w-40 h-40 bg-secondary/5 rounded-full blur-3xl"></div>
        
        {/* Contenu principal avec transition */}
        <div className="relative z-10 transition-all duration-500 transform" suppressHydrationWarning>
          {/* Étape 1: Choix entre installation et réparation */}
          {currentPage === 'home' ? (
            <div className="mt-6 animate-fadeIn" suppressHydrationWarning>
              <div className="text-center mb-8" suppressHydrationWarning>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">Quels sont vos besoins ?</h2>
                <p className="text-gray-600">Choisissez le type de service dont vous avez besoin</p>
              </div>
              
              <ServiceTypeButtons 
                activeType={null}
                onTypeChange={handleServiceTypeSelect} 
                largeButtons={true}
              />
              
              {/* Bouton Demande personnalisée */}
              <div className="mt-6 text-center" suppressHydrationWarning>
                <div className="relative" suppressHydrationWarning>
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-gray-50 text-gray-500">ou</span>
                  </div>
                </div>
                
                <button
                  onClick={handleCustomRequest}
                  className="mt-4 bg-primary text-white py-4 px-8 rounded-2xl shadow-md hover:bg-secondary transition-all duration-300 font-medium text-lg"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 inline mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Demande personnalisée
                </button>
                
                <p className="mt-2 text-sm text-gray-600">
                  Décrivez vos besoins spécifiques, vos projets de rénovation ou demandez plusieurs prestations à la fois
                </p>
              </div>
            </div>
          ) : currentPage === 'custom' ? (
            /* Formulaire de demande personnalisée */
            <div className="mt-4 animate-slideIn" suppressHydrationWarning>
              <CustomRequestForm onClose={resetSelection} />
            </div>
          ) : currentPage === 'contact' ? (
            /* Étape 4: Formulaire de contact */
            <div className="mt-4 animate-slideIn" suppressHydrationWarning>
              <ChatForm 
                serviceType={isCustomRequest ? 'reparation' : serviceType!}
                selectedService={isCustomRequest ? "Demande personnalisée" : selectedService || ""}
                serviceOption={isCustomRequest ? "Consultation et devis personnalisé" : selectedServiceOption || ""}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                onClose={backFromChat}
              />
            </div>
          ) : currentPage === 'calendar' ? (
            /* Étape 3: Calendrier de rendez-vous */
            <div className="mt-4 animate-slideIn" suppressHydrationWarning>
              <AppointmentCalendar 
                serviceType={serviceType!}
                selectedService={selectedService || ""}
                serviceOption={selectedServiceOption || ""}
                onDateSelect={handleDateSelect}
                onTimeSelect={handleTimeSelect}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                onContinue={handleProceedToContact}
              />
            </div>
          ) : currentPage === 'details' && selectedService ? (
            /* Étape 2: Liste des services disponibles */
            <div className="mt-4 animate-slideIn" suppressHydrationWarning>
              <ServiceList
                serviceType={serviceType!}
                selectedService={selectedService}
                onSubServiceSelect={handleSubServiceSelect}
              />
            </div>
          ) : currentPage === 'services' ? (
            /* Étape 1.5: Choix du service spécifique */
            <div className="mt-4 animate-slideIn" suppressHydrationWarning>
              <ServiceGrid 
                serviceType={serviceType!}
                onServiceSelect={handleServiceSelect}
                onContactClick={() => {}} // Pas utilisé dans cette version
              />
            </div>
          ) : null}
        </div>
      </main>
      
      <Footer />
      
      {/* CSS pour animations */}
      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideIn {
          from { 
            opacity: 0;
            transform: translateY(10px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
        
        .animate-slideIn {
          animation: slideIn 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
